/* eslint-disable indent */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { Card, Grid } from '@material-ui/core';
import { Field, FieldArray } from 'formik';
import get from 'lodash/get';
import { useTranslation } from '../../../src/i18n';
import PerformanceDate from '../../../components/PerformanceCardHeader/PerformanceDate';
import { colors, fontSize } from '../../../constants/styleConstants';
import TextButton from '../../../components/TextButton';
import DeleteFbIcon from '../../../components/Icons/DeleteFbIcon';
import FormikCheckbox from '../../../components/Fields/CheckboxField/FormikCheckbox';
import { TP } from '../../../constants/consts';
import { publishedCardFields, cardFields, getFieldName } from '../constants';
import FormikAutocompleteSelect from '../../../components/Fields/FormikAutocompleteSelect';
import PerformanceTime from '../../../components/PerformanceCardHeader/PerformanceTime';
import Button from '../../../components/Button';
import EditFbIcon from '../../../components/Icons/EditIcon';
import TitleWithInfo from '../../../components/TitleWithInfo';
import VenuesFieldsGroup from '../../../components/EditComponents/VenuesFieldsGroup';
import PerformanceTypeInfoBlock from './PerformanceTypeInfoBlock';

const { cancel } = publishedCardFields;
const { time, isTour, festival, isFestivalPart } = cardFields;

const useStyles = makeStyles(theme => ({
  root: {
    overflow: 'visible',
  },
  date: {
    height: 'auto',
  },
  mainInfoBlock: {
    display: 'flex',
    backgroundColor: colors.background.grayCard,
  },
  content: {
    padding: theme.spacing(1),
  },
  addTimebutton: {
    fontSize: fontSize.medium,
    fontWeight: 'bold',
    padding: '2px 15px',
    minWidth: 'auto',
  },
  timeBox: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(0.5),
    },
  },
  statusesBlock: {
    padding: theme.spacing(1),
    paddingLeft: 90,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      paddingLeft: theme.spacing(2),
      alignItems: 'center',
    },
  },
  typesBlock: {
    display: 'flex',
    flexWrap: 'wrap',
    wordBreak: 'break-word',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  typeInfoFieldsBlock: {
    width: '50%',
    minWidth: 300,
    maxWidth: 500,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  editCastBtn: {
    color: colors.blackMineShaft,
    padding: '20px',
  },
  livestreamRecord: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  infoSectionButton: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const PerformanceEditCard = ({
  className,
  deletePerformance,
  performanceFormValues,
  addTime,
  deleteTime,
  searchVenue,
  searchCities,
  loadingValue,
  venues,
  cities,
  parentFieldName,
  festivalsOptions,
  setFieldValue,
  clearValues,
  openCastModal,
  searchStages,
  stages,
  canBeDeleted,
  isFestival,
}) => {
  const { status, date, isPublished } = performanceFormValues;
  const classes = useStyles({ status });
  const { t } = useTranslation();
  const timeArray = [...get(performanceFormValues, time.name, [])];
  const isNullTime = timeArray.length === 1 && timeArray[0]?.time === null;
  // not to display null time
  if (isNullTime) {
    timeArray.pop();
  }

  return (
    <Card className={cn(classes.root, className)}>
      <div className={classes.mainInfoBlock}>
        <PerformanceDate date={date || ''} status={status} className={classes.date} />
        <Grid container className={classes.content} justify="space-between" alignItems="center">
          <Grid item sm={10} xs={12}>
            <VenuesFieldsGroup
              parentFieldName={parentFieldName}
              loadingValue={loadingValue}
              searchVenue={searchVenue}
              searchCities={searchCities}
              searchStages={searchStages}
              setFieldValue={setFieldValue}
              venues={venues}
              cities={cities}
              stages={stages}
              values={performanceFormValues}
              clearValues={clearValues}
            />
            <div>
              <TitleWithInfo title={`${TP}.PERFORMANCE_TIME`} tooltip={`${TP}.FN_PERFORMANCE_TIME_INFO`} />
              <div className={classes.timeBox}>
                <FieldArray name={getFieldName(parentFieldName, time)}>
                  {arrayHelpers => (
                    <>
                      {timeArray.map((timeInfo, index) => {
                        const timeValue = timeInfo?.time;
                        return (
                          <PerformanceTime
                            time={timeValue}
                            active
                            key={`${date}.${timeValue}`}
                            edit
                            deleteFunc={e =>
                              deleteTime(
                                e,
                                {
                                  ...performanceFormValues,
                                  ...timeInfo,
                                  index,
                                },
                                () => arrayHelpers.remove(index),
                                isPublished,
                              )
                            }
                          />
                        );
                      })}
                      {
                        <Button
                          variant="outlined"
                          onClick={e => {
                            addTime(e, isNullTime ? arrayHelpers.replace : arrayHelpers.push, performanceFormValues);
                          }}
                          className={classes.addTimebutton}
                        >
                          {t(`${TP}.FN_ADD_TIME`)}
                        </Button>
                      }
                    </>
                  )}
                </FieldArray>
              </div>
            </div>
          </Grid>
          {canBeDeleted && (
            <Grid item>
              <TextButton startIcon={<DeleteFbIcon />} onClick={e => deletePerformance(e, date)}>
                {t(`${TP}.m_DELETE`)}
              </TextButton>
            </Grid>
          )}
        </Grid>
      </div>
      <div className={classes.statusesBlock}>
        <div>
          <div className={classes.typesBlock}>
            <Field name={getFieldName(parentFieldName, cancel)} label={t(cancel.label)} component={FormikCheckbox} />
            <Field
              name={getFieldName(parentFieldName, isTour)}
              label={t(`${TP}.FN_EDITSEASON_TOUR`)}
              component={FormikCheckbox}
            />
            {isFestival && (
              <Field
                name={getFieldName(parentFieldName, isFestivalPart)}
                label={t(`${TP}.m_FESTIVAL`)}
                component={FormikCheckbox}
              />
            )}
          </div>
          <div className={classes.typeInfoFieldsBlock}>
            {!!festivalsOptions?.length && isFestival && (
              <PerformanceTypeInfoBlock title={t(`${TP}.m_FESTIVAL`)}>
                <Field
                  instanceId={getFieldName(parentFieldName, festival)}
                  name={getFieldName(parentFieldName, festival)}
                  label={t(`${TP}.FN_FESTIVAL_NAME`)}
                  component={FormikAutocompleteSelect}
                  options={festivalsOptions}
                />
              </PerformanceTypeInfoBlock>
            )}
          </div>
        </div>
        <div>
          {' '}
          <TextButton
            className={classes.editCastBtn}
            startIcon={<EditFbIcon />}
            onClick={e => {
              openCastModal(e, get(performanceFormValues, time.name, []), performanceFormValues);
            }}
          >
            {t(`${TP}.FN_EDIT_PERFORMANCE_CAST`)}
          </TextButton>
        </div>
      </div>
    </Card>
  );
};

PerformanceEditCard.propTypes = {
  className: PropTypes.string,
  deletePerformance: PropTypes.func,
  performanceFormValues: PropTypes.object.isRequired,
  loadingValue: PropTypes.string,
  venues: PropTypes.array,
  festivalsOptions: PropTypes.array,
  searchVenue: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  searchStages: PropTypes.func.isRequired,
  stages: PropTypes.array,
  canBeDeleted: PropTypes.bool.isRequired,
};

PerformanceEditCard.defaultProps = {
  deletePerformance: () => {},
  loadingValue: '',
  venues: [],
  festivalsOptions: [],
  searchVenue: () => {},
  stages: [],
};

export default memo(PerformanceEditCard);
